import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  DatePicker,
  Dropdown,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import type { StylesConfig } from "react-select";
import "../../../../../create-new-partner/partner-detail-form/PartnerDetailForm.scss";
import "../../../partner-list/partner-filter-modal/PartnerFilterModal.scss";

export type BenefitsLogFilterState = {
  partner: string[];
  category: string[];
  benefitsName: string[];
  status: string[];
  hidOwner: string[];
  partnerOwner: string[];
  deliveredDateRange: string[];
  plannedDateRange: string[];
};

export const EMPTY_BENEFITS_LOG_FILTER: BenefitsLogFilterState = {
  partner: [],
  category: [],
  benefitsName: [],
  status: [],
  hidOwner: [],
  partnerOwner: [],
  deliveredDateRange: [],
  plannedDateRange: [],
};

type Option = { label: string; value: string };

type DateState = {
  deliveredFrom: Date | null;
  deliveredTo: Date | null;
  plannedFrom: Date | null;
  plannedTo: Date | null;
};

type Props = {
  filterProp: BenefitsLogFilterState;
  setFilterProp: (filter: BenefitsLogFilterState) => void;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** When omitted, the Category dropdown is hidden. */
  categoryOptions?: Option[];
  benefitNameOptions: Option[];
  statusOptions: Option[];
  /** When provided, a Partner dropdown is shown above the log fields. */
  partnerOptions?: Option[];
  /** When provided, an HID Owner(s) dropdown is shown after Status. */
  hidOwnerOptions?: Option[];
  /** When provided, a Partner Owner(s) dropdown is shown after HID Owner(s). */
  partnerOwnerOptions?: Option[];
  /**
   * Keep a date range with only "from" or only "to" as `[from, ""]` / `["", to]`
   * instead of collapsing it to a single day.
   */
  keepOpenEndedDateRanges?: boolean;
  title?: string;
};

const FILTER_DRAWER_Z_INDEX = 1300;
const DROPDOWN_OPTION_HOVER_BG = "#e6e6e6";

const filterDropdownMenuStyles: StylesConfig<Option, true> = {
  menuPortal: (base) => ({
    ...base,
    zIndex: FILTER_DRAWER_Z_INDEX,
  }),
  menu: (base) => ({
    ...base,
    zIndex: FILTER_DRAWER_Z_INDEX + 1,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? DROPDOWN_OPTION_HOVER_BG
      : state.isSelected
        ? base.backgroundColor
        : "transparent",
    color: base.color,
    ":active": {
      backgroundColor: DROPDOWN_OPTION_HOVER_BG,
    },
  }),
};

const FilterSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="partner-form-section">
    <h3 className="partner-form-section__title">{title}</h3>
    <div className="partner-form-section__card">{children}</div>
  </section>
);

const normalizeDateRange = (range: string[]): string[] => {
  if (range[0] && !range[1]) return [range[0], range[0]];
  if (!range[0] && range[1]) return [range[1], range[1]];
  return range;
};

const BenefitsLogFilterModal: React.FC<Props> = ({
  filterProp,
  setFilterProp,
  setModalOpen,
  categoryOptions,
  benefitNameOptions,
  statusOptions,
  partnerOptions,
  hidOwnerOptions,
  partnerOwnerOptions,
  keepOpenEndedDateRanges = false,
  title = "Filter Log History",
}) => {
  const [filter, setFilter] = useState<BenefitsLogFilterState>({
    ...EMPTY_BENEFITS_LOG_FILTER,
    ...filterProp,
  });
  const [dates, setDates] = useState<DateState>(() => ({
    deliveredFrom: filterProp.deliveredDateRange[0]
      ? new Date(filterProp.deliveredDateRange[0])
      : null,
    deliveredTo: filterProp.deliveredDateRange[1]
      ? new Date(filterProp.deliveredDateRange[1])
      : null,
    plannedFrom: filterProp.plannedDateRange[0]
      ? new Date(filterProp.plannedDateRange[0])
      : null,
    plannedTo: filterProp.plannedDateRange[1]
      ? new Date(filterProp.plannedDateRange[1])
      : null,
  }));

  useEffect(() => {
    document.body.classList.add("partner-filter-drawer-open");
    return () => {
      document.body.classList.remove("partner-filter-drawer-open");
    };
  }, []);

  const selectedPartners = useMemo(
    () =>
      (partnerOptions || []).filter((option) =>
        filter.partner.includes(option.value)
      ),
    [partnerOptions, filter.partner]
  );

  const selectedCategory = useMemo(
    () =>
      (categoryOptions ?? []).filter((option) =>
        filter.category.includes(option.value)
      ),
    [categoryOptions, filter.category]
  );

  const selectedBenefits = useMemo(
    () =>
      benefitNameOptions.filter((option) =>
        filter.benefitsName.includes(option.value)
      ),
    [benefitNameOptions, filter.benefitsName]
  );

  const selectedStatus = useMemo(
    () => statusOptions.filter((option) => filter.status.includes(option.value)),
    [filter.status, statusOptions]
  );

  const selectedHidOwners = useMemo(
    () =>
      (hidOwnerOptions || []).filter((option) =>
        filter.hidOwner.includes(option.value)
      ),
    [hidOwnerOptions, filter.hidOwner]
  );

  const selectedPartnerOwners = useMemo(
    () =>
      (partnerOwnerOptions || []).filter((option) =>
        filter.partnerOwner.includes(option.value)
      ),
    [partnerOwnerOptions, filter.partnerOwner]
  );

  const formatDateForFilter = useCallback((date: Date | null) => {
    return date ? date.toLocaleDateString("en-CA") : null;
  }, []);

  const handleDropdownChange = useCallback(
    (data: Option[] | null | undefined, field: keyof BenefitsLogFilterState) => {
      setFilter((prev) => ({
        ...prev,
        [field]: (data || []).map((item) => item.value),
      }));
    },
    []
  );

  const handleDateChange = useCallback(
    (date: Date | [Date | null, Date | null] | null, name: keyof DateState) => {
      const actualDate = Array.isArray(date) ? date[0] : date;
      setDates((prev) => ({ ...prev, [name]: actualDate }));

      const formattedDate = formatDateForFilter(actualDate);
      setFilter((prev) => {
        const next = { ...prev };
        switch (name) {
          case "deliveredFrom":
            next.deliveredDateRange = formattedDate
              ? [formattedDate, prev.deliveredDateRange[1] || ""]
              : prev.deliveredDateRange[1]
                ? ["", prev.deliveredDateRange[1]]
                : [];
            if (!next.deliveredDateRange[0] && !next.deliveredDateRange[1]) {
              next.deliveredDateRange = [];
            }
            break;
          case "deliveredTo":
            next.deliveredDateRange = formattedDate
              ? [prev.deliveredDateRange[0] || "", formattedDate]
              : prev.deliveredDateRange[0]
                ? [prev.deliveredDateRange[0], ""]
                : [];
            if (!next.deliveredDateRange[0] && !next.deliveredDateRange[1]) {
              next.deliveredDateRange = [];
            }
            break;
          case "plannedFrom":
            next.plannedDateRange = formattedDate
              ? [formattedDate, prev.plannedDateRange[1] || ""]
              : prev.plannedDateRange[1]
                ? ["", prev.plannedDateRange[1]]
                : [];
            if (!next.plannedDateRange[0] && !next.plannedDateRange[1]) {
              next.plannedDateRange = [];
            }
            break;
          case "plannedTo":
            next.plannedDateRange = formattedDate
              ? [prev.plannedDateRange[0] || "", formattedDate]
              : prev.plannedDateRange[0]
                ? [prev.plannedDateRange[0], ""]
                : [];
            if (!next.plannedDateRange[0] && !next.plannedDateRange[1]) {
              next.plannedDateRange = [];
            }
            break;
          default:
            break;
        }
        return next;
      });
    },
    [formatDateForFilter]
  );

  const onClearAllClick = useCallback(() => {
    setFilter({ ...EMPTY_BENEFITS_LOG_FILTER });
    setDates({
      deliveredFrom: null,
      deliveredTo: null,
      plannedFrom: null,
      plannedTo: null,
    });
  }, []);

  const onApplyClick = useCallback(() => {
    const updatedFilter: BenefitsLogFilterState = keepOpenEndedDateRanges
      ? filter
      : {
          ...filter,
          deliveredDateRange: normalizeDateRange(
            filter.deliveredDateRange.filter(Boolean)
          ),
          plannedDateRange: normalizeDateRange(
            filter.plannedDateRange.filter(Boolean)
          ),
        };
    setFilterProp(updatedFilter);
    setModalOpen(false);
  }, [filter, keepOpenEndedDateRanges, setFilterProp, setModalOpen]);

  return createPortal(
    <>
      <div className="partner-filter-drawer__backdrop" aria-hidden="true" />
      <div
        className="partner-filter-drawer partner-filter-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="partner-filter-drawer__header">
          <div className="partner-filter-drawer__header-title">
            <span
              className="partner-filter-drawer__filter-icon"
              aria-hidden="true"
            >
              <Iconography icon="sliders" size={IconographySizes.Medium} />
            </span>
            <span>{title}</span>
          </div>
          <button
            type="button"
            className="partner-filter-drawer__close-btn"
            onClick={() => setModalOpen(false)}
            aria-label="Close filter panel"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </div>

        <div className="partner-filter-drawer__body partner-detail-form-container partner-detail-form-container--modal">
          <FilterSection title="Log Details">
            {partnerOptions && (
              <div className="label-field-container">
                <label className="field-label" htmlFor="bl-filter-partner">
                  Partner
                </label>
                <Dropdown
                  key={`partner-${partnerOptions.length}`}
                  label=""
                  id="bl-filter-partner"
                  placeholder="Select"
                  options={partnerOptions}
                  isClearable
                  isMulti
                  isSearchable
                  selected={selectedPartners}
                  onSelect={(data) =>
                    handleDropdownChange((data as Option[]) || [], "partner")
                  }
                  menuPortalTarget={document.body}
                  styles={filterDropdownMenuStyles}
                />
              </div>
            )}

            {categoryOptions && (
              <div className="label-field-container">
                <label className="field-label" htmlFor="bl-filter-category">
                  Category
                </label>
                <Dropdown
                  key={`category-${categoryOptions.length}`}
                  label=""
                  id="bl-filter-category"
                  placeholder="Select"
                  options={categoryOptions}
                  isClearable
                  isMulti
                  isSearchable
                  selected={selectedCategory}
                  onSelect={(data) =>
                    handleDropdownChange(data as Option[], "category")
                  }
                  menuPortalTarget={document.body}
                  styles={filterDropdownMenuStyles}
                />
              </div>
            )}

            <div className="label-field-container">
              <label className="field-label" htmlFor="bl-filter-benefits-name">
                Benefits Name
              </label>
              <Dropdown
                key={`benefits-${benefitNameOptions.length}`}
                label=""
                id="bl-filter-benefits-name"
                placeholder="Select"
                options={benefitNameOptions}
                isClearable
                isMulti
                isSearchable
                selected={selectedBenefits}
                onSelect={(data) =>
                  handleDropdownChange((data as Option[]) || [], "benefitsName")
                }
                menuPortalTarget={document.body}
                styles={filterDropdownMenuStyles}
              />
            </div>

            <div className="label-field-container">
              <label className="field-label" htmlFor="bl-filter-status">
                Status
              </label>
              <Dropdown
                key={`status-${statusOptions.length}`}
                label=""
                id="bl-filter-status"
                placeholder="Select"
                options={statusOptions}
                isClearable
                isMulti
                isSearchable
                selected={selectedStatus}
                onSelect={(data) =>
                  handleDropdownChange((data as Option[]) || [], "status")
                }
                menuPortalTarget={document.body}
                styles={filterDropdownMenuStyles}
              />
            </div>

            {hidOwnerOptions && (
              <div className="label-field-container">
                <label className="field-label" htmlFor="bl-filter-hid-owner">
                  HID Owner(s)
                </label>
                <Dropdown
                  key={`hid-owner-${hidOwnerOptions.length}`}
                  label=""
                  id="bl-filter-hid-owner"
                  placeholder="Select"
                  options={hidOwnerOptions}
                  isClearable
                  isMulti
                  isSearchable
                  selected={selectedHidOwners}
                  onSelect={(data) =>
                    handleDropdownChange((data as Option[]) || [], "hidOwner")
                  }
                  menuPortalTarget={document.body}
                  styles={filterDropdownMenuStyles}
                />
              </div>
            )}

            {partnerOwnerOptions && (
              <div className="label-field-container">
                <label className="field-label" htmlFor="bl-filter-partner-owner">
                  Partner Owner(s)
                </label>
                <Dropdown
                  key={`partner-owner-${partnerOwnerOptions.length}`}
                  label=""
                  id="bl-filter-partner-owner"
                  placeholder="Select"
                  options={partnerOwnerOptions}
                  isClearable
                  isMulti
                  isSearchable
                  selected={selectedPartnerOwners}
                  onSelect={(data) =>
                    handleDropdownChange((data as Option[]) || [], "partnerOwner")
                  }
                  menuPortalTarget={document.body}
                  styles={filterDropdownMenuStyles}
                />
              </div>
            )}

            <div className="partner-form-grid">
              <div className="partner-registration-date-field">
                <label className="field-label" htmlFor="bl-delivered-from">
                  Delivered Date From
                </label>
                <DatePicker
                  label=""
                  onChange={(date) => handleDateChange(date, "deliveredFrom")}
                  format="dd/MM/yyyy"
                  placeholder="dd/mm/yyyy"
                  id="bl-delivered-from"
                  name="bl-delivered-from"
                  value={dates.deliveredFrom}
                  isClearable
                />
              </div>
              <div className="partner-registration-date-field">
                <label className="field-label" htmlFor="bl-delivered-to">
                  Delivered Date To
                </label>
                <DatePicker
                  label=""
                  onChange={(date) => handleDateChange(date, "deliveredTo")}
                  format="dd/MM/yyyy"
                  placeholder="dd/mm/yyyy"
                  id="bl-delivered-to"
                  name="bl-delivered-to"
                  value={dates.deliveredTo}
                  isClearable
                />
              </div>
              <div className="partner-registration-date-field">
                <label className="field-label" htmlFor="bl-planned-from">
                  Planned Date From
                </label>
                <DatePicker
                  label=""
                  onChange={(date) => handleDateChange(date, "plannedFrom")}
                  format="dd/MM/yyyy"
                  placeholder="dd/mm/yyyy"
                  id="bl-planned-from"
                  name="bl-planned-from"
                  value={dates.plannedFrom}
                  isClearable
                />
              </div>
              <div className="partner-registration-date-field">
                <label className="field-label" htmlFor="bl-planned-to">
                  Planned Date To
                </label>
                <DatePicker
                  label=""
                  onChange={(date) => handleDateChange(date, "plannedTo")}
                  format="dd/MM/yyyy"
                  placeholder="dd/mm/yyyy"
                  id="bl-planned-to"
                  name="bl-planned-to"
                  value={dates.plannedTo}
                  isClearable
                />
              </div>
            </div>
          </FilterSection>
        </div>

        <div className="partner-filter-drawer__footer">
          <Button
            variant="secondary"
            label="CANCEL"
            onClick={() => setModalOpen(false)}
          />
          <div className="partner-filter-drawer__footer-right">
            <Button
              variant="secondary"
              label="RESET"
              onClick={onClearAllClick}
            />
            <Button variant="primary" label="APPLY" onClick={onApplyClick} />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default BenefitsLogFilterModal;
